"use client";

import * as stylex from "@stylexjs/stylex";
import { Check, ChevronLeft, ChevronRight, Image, Maximize, Pause, Pencil, Play, Trash2 } from "lucide-react";
import { useCallback, useState } from "react";
import { copy } from "@/copy";
import type { Photo } from "@/domain/schemas";
import "@/theme/tokens.stylex";
import { colors, radii, space } from "@/theme/tokens.stylex";
import "@/theme/type.stylex";
import { type } from "@/theme/type.stylex";
import { Button } from "@/ui/Button";
import { Eyebrow } from "@/ui/Eyebrow";
import { Icon } from "@/ui/Icon";
import { IconButton } from "@/ui/IconButton";
import { photoHint, photoMeta } from "../slideshow";
type FeaturedPhotoProps = {
  photo: Photo;
  src: string;
  thisYear: number;
  playing: boolean;
  isBackground: boolean;
  online: boolean;
  deleting: boolean;
  onEdit: () => void;
  onAskDelete: () => void;
  onCancelDelete: () => void;
  onDelete: () => void;
  onStep: (delta: number) => void;
  onTogglePlay: () => void;
  onFullScreen: () => void;
  onSetBackground: () => void;
  onOpenBackground: () => void;
  frame: stylex.StyleXStyles;
};
type PhotoActionsProps = Pick<FeaturedPhotoProps, "isBackground" | "online" | "deleting" | "onEdit" | "onAskDelete" | "onCancelDelete" | "onDelete" | "onSetBackground" | "onOpenBackground">;
type SlideshowButtonsProps = Pick<FeaturedPhotoProps, "playing" | "onStep" | "onTogglePlay" | "onFullScreen">;
const styles = {
  image: {
    kzQI83: "x1iyjqo2",
    kmuXW: "xs83m0k",
    kCS8Yb: "x1r8uery",
    kzqmXN: "xh8yej3",
    kZKoxP: "xqtp20y",
    k7Eaqz: "xeuugli",
    kVIFPx: "xl1xv1r",
    kWkggS: "xh540kj",
    kaIpWk: "x1fv5t7x",
    k1xSpc: "x1lliihq",
    kkrTdU: "x1huxd7x",
    $$css: true
  },
  portrait: {
    kVIFPx: "x19kjcj4",
    $$css: true
  },
  waiting: {
    kzQI83: "x1iyjqo2",
    kmuXW: "xs83m0k",
    kCS8Yb: "x1t1x2f9",
    kzqmXN: "xh8yej3",
    kaIpWk: "x1fv5t7x",
    kWkggS: "xh540kj",
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kGNEyG: "x6s0dn4",
    kjj79g: "xl56j7k",
    kOIVth: "x1hu4l6k",
    kmVPX3: "x18ppelm",
    kB7OPa: "x9f619",
    k9WMMc: "x2b8uid",
    $$css: true
  }
};
export function FeaturedPhoto({
  photo,
  src,
  thisYear,
  frame,
  playing,
  onStep,
  onTogglePlay,
  onFullScreen,
  ...actions
}: FeaturedPhotoProps) {
  const hint = photoHint(photo);
  const [portraitSrc, setPortraitSrc] = useState<string>();
  const measure = useCallback((image: HTMLImageElement | null) => {
    if (image?.complete && image.naturalWidth > 0) setPortraitSrc(isPortrait(image) ? src : undefined);
  }, [src]);
  return <>
      {/* biome-ignore lint/performance/noImgElement: photos come pre-sized from the authenticated file route and the service worker cache, not the Next image optimizer */}
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: the Full screen button is the keyboard path */}
      <img src={src} alt={photo.title} onClick={onFullScreen} ref={measure} onLoad={event => measure(event.currentTarget)} {...stylex.props(styles.image, portraitSrc === src && styles.portrait, frame)} />
      <div className="x78zum5 xdt5ytf x1hu4l6k xeuugli">
        <div className="x78zum5 xdt5ytf x17d4w8g xeuugli">
          <Eyebrow>{photoMeta(photo, thisYear)}</Eyebrow>
          <h2 className="x1ghz6dp xuaujqh x1v94iar xipi2l9 x1159mfc">{photo.title}</h2>
          {hint && <span className="x1j61zf2 x15i68ds x37zpob x1fzhlzt xq2pcee">{hint}</span>}
        </div>
        <div className="x78zum5 x1a02dak x1qughib x6s0dn4 x1dawklh xzdcfao">
          <PhotoActions {...actions} />
          <SlideshowButtons playing={playing} onStep={onStep} onTogglePlay={onTogglePlay} onFullScreen={onFullScreen} />
        </div>
      </div>
    </>;
}
export function FeaturedWaiting({
  frame
}: {
  frame: stylex.StyleXStyles;
}) {
  return <div {...stylex.props(styles.waiting, frame)}>
      <Icon icon={Image} size={40} strokeWidth={1.5} />
      <span className="xosj86m x15i68ds xfrs9s4 x1fzhlzt xxc7z9f">{copy.photos.waitingForUploads}</span>
    </div>;
}
function PhotoActions({
  isBackground,
  online,
  deleting,
  onEdit,
  onAskDelete,
  onCancelDelete,
  onDelete,
  onSetBackground,
  onOpenBackground
}: PhotoActionsProps) {
  return <div className="x78zum5 x1a02dak x6s0dn4 x1qqj5w7 x1rm44br">
      {isBackground ? <Button variant="ghost" icon={Check} onClick={onOpenBackground}>
          {copy.photos.backgroundNow}
        </Button> : <Button variant="ghost" icon={Image} onClick={onSetBackground}>
          {copy.photos.setBackground}
        </Button>}
      <Button variant="ghost" icon={Pencil} onClick={onEdit}>
        {copy.photos.edit}
      </Button>
      {online && !deleting && <Button variant="ghost" icon={Trash2} onClick={onAskDelete}>
          {copy.photos.delete}
        </Button>}
      {online && deleting && <div role="alertdialog" aria-label={copy.photos.deleteName} className="x78zum5 x6s0dn4 x152posx xgkuqjh">
          <Button variant="danger" icon={Trash2} onClick={onDelete}>
            {copy.photos.confirmDelete}
          </Button>
          <Button variant="ghost" onClick={onCancelDelete}>
            {copy.photos.cancelDelete}
          </Button>
        </div>}
    </div>;
}
function SlideshowButtons({
  playing,
  onStep,
  onTogglePlay,
  onFullScreen
}: SlideshowButtonsProps) {
  return (
    // biome-ignore lint/a11y/useSemanticElements: the slideshow buttons are a toolbar row, not a form fieldset
    <div role="group" aria-label={copy.photos.slideshow} className="x78zum5 x152posx">
      <IconButton icon={ChevronLeft} label={copy.photos.previous} variant="canvas" size="touch" onClick={() => onStep(-1)} />
      <IconButton icon={playing ? Pause : Play} label={playing ? copy.photos.pause : copy.photos.play} variant="solid" size="touch" onClick={onTogglePlay} />
      <IconButton icon={ChevronRight} label={copy.photos.next} variant="canvas" size="touch" onClick={() => onStep(1)} />
      <IconButton icon={Maximize} label={copy.photos.fullScreen} variant="canvas" size="touch" onClick={onFullScreen} />
    </div>
  );
}
function isPortrait(image: HTMLImageElement): boolean {
  return image.naturalHeight > image.naturalWidth;
}